import type { Permission, Role } from '@talentbabu/types';
import { Spinner } from '@talentbabu/ui';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { homePathFor, useAuthStore, userCan } from '../../app/auth-store';
import { ForbiddenPage } from '../errors/ErrorPages';

function FullPageSpinner() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

/**
 * Client-side route protection improves UX only. Every API endpoint enforces
 * authentication and permissions independently.
 */
export function RequireAuth({ roles }: { roles?: readonly Role[] }) {
  const { status, user } = useAuthStore();
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (roles && user.role !== 'SUPER_ADMIN' && !roles.includes(user.role)) return <ForbiddenPage />;
  return <Outlet />;
}

export function RequirePermission({ permission, children }: { permission: Permission; children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  return userCan(user, permission) ? <>{children}</> : <ForbiddenPage />;
}

/** Login/register pages redirect signed-in users to their dashboard. */
export function GuestOnly() {
  const { status, user } = useAuthStore();
  if (status === 'loading') return <FullPageSpinner />;
  if (user) return <Navigate to={homePathFor(user.role)} replace />;
  return <Outlet />;
}

export function Can({ permission, children }: { permission: Permission; children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  return userCan(user, permission) ? <>{children}</> : null;
}
